import { memo, useContext } from "react";
import "./PaymentDueSummary.scss";
import { FormattedDate, FormattedMessage } from "react-intl";
import propTypes from "prop-types";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { PAYMENT_TERMS } from "app/constants";
import Total from "app/pages/.shared/PriceSummary/Total/Total";
import SeparatorLine from "app/components/ui/Separator/SeparatorLine";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import SavingsHighlightContainer from "app/pages/Booking/Quotation/PriceDetails/SavingsHighlightContainer";
import { calculateDiscountPercentage } from "app/utils/utils";

const PaymentDueSummary = ({
	payNowAmount,
	payAtPropertyAmount,
	paymentMode,
	mensualite2Fois,
	total,
	savingsTotalAmount = 0,
	showPayNowBloc = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	const publicPrice = savingsTotalAmount > 0 ? total + savingsTotalAmount : 0;

	const discountPercentage = calculateDiscountPercentage({
		publicPrice,
		savingsTotalAmount,
	});

	if (!payNowAmount) {
		return null;
	}

	const is2xPaymentMode = paymentMode === PAYMENT_TERMS["2x"];

	const secondPaymentDueDate = is2xPaymentMode && mensualite2Fois?.dueDate;
	const secondPaymentAmount = is2xPaymentMode && mensualite2Fois?.payLaterAmount;

	const parsedSecondPaymentDueDate = new Date(secondPaymentDueDate);
	const isSecondPaymentDueDateValid = !isNaN(parsedSecondPaymentDueDate.getTime());

	return (
		<div className="payment-due-summary" data-testId="payment-due-summary">
			{!isMobile && showPayNowBloc && (
				<>
					<div className="payment-due-summary__group">
						<div className="payment-due-summary__item">
							<Total
								total={total}
								discountPercentage={discountPercentage}
								taxesAndFeesIncluded
							/>
						</div>
						<div className="payment-due-summary__item payment-due-summary__item--savings">
							<SavingsHighlightContainer
								messageKey="general.savings.realized.with.amount"
								trackEvent="booking_click_savings_realized"
								trackData={{
									publicPrice,
									discountPercentage,
								}}
								description={
									<div>
										<div className="savings-highlight__description-title">
											<FormattedMessage id="savings.tooltip.title" />
										</div>
										<ul className="savings-highlight__list">
											<li>
												<FormattedMessage id="savings.tooltip.line1" />
											</li>
											<li>
												<FormattedMessage id="savings.tooltip.line2" />
											</li>
										</ul>
									</div>
								}
							/>
							{publicPrice > 0 && (
								<div className="payment-due-summary__reference">
									<FormattedMessage
										id="general.instead.of.price.with.amount"
										values={{
											price: <AmountContainer amount={publicPrice} />,
										}}
									/>
								</div>
							)}
						</div>
					</div>
					<SeparatorLine />
				</>
			)}
			<div className="payment-due-summary__group">
				<div className="payment-due-summary__item">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="p" isBold>
						<FormattedMessage id="my.bookings.pay.now.label" />
					</Typography>

					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="p" isBold>
						<AmountContainer amount={payNowAmount} />
					</Typography>
				</div>

				{isSecondPaymentDueDateValid && secondPaymentAmount > 0 && (
					<div className="payment-due-summary__item">
						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<FormattedMessage
								id="payment.pay.later.amount"
								values={{
									dueDate: (
										<FormattedDate
											timeZone="UTC"
											value={parsedSecondPaymentDueDate}
											year="numeric"
											month="2-digit"
											day="2-digit"
										/>
									),
								}}
							/>
						</Typography>

						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<AmountContainer
								className="amount--normal"
								amount={secondPaymentAmount}
							/>
						</Typography>
					</div>
				)}

				{payAtPropertyAmount > 0 && (
					<div className="payment-due-summary__item">
						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<FormattedMessage id="pay.at.property" />
						</Typography>

						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<AmountContainer
								className="amount--normal"
								amount={payAtPropertyAmount}
							/>
						</Typography>
					</div>
				)}
			</div>
		</div>
	);
};

PaymentDueSummary.propTypes = {
	payNowAmount: propTypes.number,
	payAtPropertyAmount: propTypes.number,
	paymentMode: propTypes.number,
	mensualite2Fois: propTypes.object,
	total: propTypes.number,
	savingsTotalAmount: propTypes.number,
	showPayNowBloc: propTypes.bool,
};

export default memo(PaymentDueSummary);
